import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../api";
import { PageHeader, Badge, eur, Btn } from "../../components/common";
import { Plus, Target, Filter, Search, Building2, TrendingUp } from "lucide-react";
import ProposalDrawer from "../../components/ProposalDrawer";
import { useAutoRefresh } from "../../lib/autoRefresh";

// Paleta de cores por plataforma - deterministica (hash do nome)
const PLATFORM_PALETTE = [
  { bg: "#dbeafe", text: "#1d4ed8", border: "#93c5fd" }, // azul
  { bg: "#dcfce7", text: "#15803d", border: "#86efac" }, // verde
  { bg: "#fef9c3", text: "#854d0e", border: "#fde047" }, // amarelo
  { bg: "#fce7f3", text: "#9d174d", border: "#f9a8d4" }, // rosa
  { bg: "#ede9fe", text: "#6d28d9", border: "#c4b5fd" }, // violeta
  { bg: "#ffedd5", text: "#9a3412", border: "#fdba74" }, // laranja
  { bg: "#cffafe", text: "#0e7490", border: "#67e8f9" }, // ciano
  { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" }, // verde-escuro
  { bg: "#fdf2f8", text: "#86198f", border: "#e879f9" }, // roxo
  { bg: "#fff7ed", text: "#7c2d12", border: "#fed7aa" }, // ambar
];

function platformColor(name) {
  if (!name) return { bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1" };
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return PLATFORM_PALETTE[h % PLATFORM_PALETTE.length];
}

// Sparkline SVG inline com tooltip ao hover e indicador de snapshot real vs reconstruido
function Sparkline({ data = [], points = [], width = 120, height = 32 }) {
  const vals = data.length ? data : points.map((p) => p.mrr ?? p);
  const [hovered, setHovered] = useState(null);
  if (!vals || vals.length < 2) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const pad = 2;
  const w = width - pad * 2;
  const h = height - pad * 2;
  const coords = vals.map((v, i) => ({
    x: pad + (i / (vals.length - 1)) * w,
    y: pad + (1 - (v - min) / range) * h,
    v,
    label: points[i]?.week ?? null,
    real: points[i]?.real ?? false,
    count: points[i]?.count ?? null,
  }));
  const lastUp = vals[vals.length - 1] >= vals[vals.length - 2];
  const lineColor = lastUp ? "#22c55e" : "#ef4444";
  const ptsStr = coords.map((c) => `${c.x},${c.y}`).join(" ");
  const fillPath = `M${coords.map((c) => `${c.x},${c.y}`).join("L")}L${pad + w},${pad + h}L${pad},${pad + h}Z`;
  const hov = hovered !== null ? coords[hovered] : null;
  return (
    <div className="relative inline-flex">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
        className="overflow-visible cursor-crosshair"
        onMouseLeave={() => setHovered(null)}>
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.18" />
            <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillPath} fill="url(#spark-fill)" />
        <polyline points={ptsStr} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {/* Pontos visiveis - real=solido, reconstruido=anel vazado */}
        {coords.map((c, i) => (
          c.real ? (
            <circle key={i} cx={c.x} cy={c.y} r="2" fill={lineColor} stroke="white" strokeWidth="1" />
          ) : (
            <circle key={i} cx={c.x} cy={c.y} r="2" fill="white" stroke={lineColor} strokeWidth="1" strokeDasharray="1.5,1" opacity="0.7" />
          )
        ))}
        {/* Areas de hover invisiveis */}
        {coords.map((c, i) => (
          <circle key={`h${i}`} cx={c.x} cy={c.y} r="7" fill="transparent"
            onMouseEnter={() => setHovered(i)} />
        ))}
        {/* Ponto hovered - destaque */}
        {hov && (
          <>
            <line x1={hov.x} y1={pad} x2={hov.x} y2={pad + h}
              stroke={lineColor} strokeWidth="1" strokeDasharray="2,2" opacity="0.5" />
            <circle cx={hov.x} cy={hov.y} r="4" fill={lineColor} stroke="white" strokeWidth="1.5" />
          </>
        )}
      </svg>
      {/* Tooltip */}
      {hov && (
        <div className="absolute z-50 pointer-events-none"
          style={{ left: hov.x + 8, top: hov.y - 48, transform: hov.x > width * 0.65 ? "translateX(-110%)" : "none" }}>
          <div className="bg-slate-900 text-white text-[10px] rounded-lg px-2.5 py-1.5 shadow-xl whitespace-nowrap leading-snug">
            {hov.label && (
              <div className="text-slate-400 text-[9px] uppercase tracking-wide mb-0.5">semana {hov.label}</div>
            )}
            <div className="font-bold text-[12px]">
              {hov.v.toLocaleString("pt-PT", { style: "currency", currency: "EUR" })}/mes
            </div>
            {hov.count !== null && (
              <div className="text-slate-400 text-[9px]">{hov.count} deal{hov.count !== 1 ? "s" : ""} ativos</div>
            )}
            <div className={`flex items-center gap-1 mt-1 text-[9px] font-medium ${hov.real ? "text-emerald-400" : "text-amber-400"}`}>
              {hov.real ? (
                <><svg width="8" height="8" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3.5" fill="#22c55e" /><polyline points="2,4 3.5,5.5 6,2.5" fill="none" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg> snapshot real</>
              ) : (
                <><svg width="8" height="8" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3.5" fill="none" stroke="#f59e0b" strokeWidth="1.2" strokeDasharray="2,1"/></svg> valor estimado</>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Kanban de Oportunidades (funis).
 * Le dinamicamente todos os pipelines de /api/pipelines e mostra por etapas.
 * Drag-and-drop entre colunas altera `estagio` via PUT /crm/deals/{id}.
 * Compativel com qualquer funil futuro criado em Definicoes > Funis.
 */
export default function Opportunities() {
  const nav = useNavigate();
  const [deals, setDeals] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState(() => localStorage.getItem("opps.pipeline") || "");
  const [q, setQ] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("");
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [previewProp, setPreviewProp] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [mrrHistory, setMrrHistory] = useState([]);
  const [mrrHistoryByPipeline, setMrrHistoryByPipeline] = useState({}); // { pipeline_id: [...] }
  const [sparkPipeline, setSparkPipeline] = useState(""); // "" = global

  const load = async () => {
    try {
      const [d, p, a, h] = await Promise.all([
        api.get("/crm/deals"),
        api.get("/pipelines"),
        api.get("/crm/accounts"),
        api.get("/crm/deals/mrr-history?weeks=10"),
      ]);
      setDeals(d.data || []);
      setPipelines(p.data || []);
      setAccounts(a.data || []);
      setMrrHistory(h.data || []);
      if (!pipelineId && (p.data || []).length) setPipelineId(p.data[0].id);
      // Carregar historico por pipeline se houver mais de 1
      if ((p.data || []).length > 1) {
        const perPipeline = {};
        await Promise.all((p.data || []).map(async (pl) => {
          try {
            const r = await api.get(`/crm/deals/mrr-history?weeks=10&pipeline_id=${pl.id}`);
            perPipeline[pl.id] = r.data || [];
          } catch { /* silencioso */ }
        }));
        setMrrHistoryByPipeline(perPipeline);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load); // eslint-disable-line
  useEffect(() => { if (pipelineId) localStorage.setItem("opps.pipeline", pipelineId); }, [pipelineId]);

  const move = async (id, estagio) => {
    setDeals((rows) => rows.map((r) => (r.id === id ? { ...r, estagio } : r)));
    try {
      const { data } = await api.put(`/crm/deals/${id}`, { estagio });
      if (data?.auto_proposal) {
        const p = data.auto_proposal;
        const dealRow = deals.find((x) => x.id === id);
        toast.success(`Proposta rascunho ${p.reused ? "existente" : "criada"}: ${p.numero}`, {
          action: { label: "Pre-visualizar", onClick: () => setPreviewProp({ id: p.id, numero: p.numero, titulo: p.titulo, account_id: dealRow?.account_id || "", estado: "rascunho" }) },
          duration: 10000,
        });
      }
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  const pipeline = pipelines.find((p) => p.id === pipelineId) || pipelines[0];
  const stages = useMemo(() => (pipeline?.stages || []).slice().sort((a, b) => a.ordem - b.ordem), [pipeline]);

  // Plataformas unicas para o filtro
  const platformOptions = useMemo(() => {
    const all = [...new Set(deals.map((d) => d.plataforma).filter(Boolean))].sort();
    return all;
  }, [deals]);

  const filtered = useMemo(() => {
    let rows = deals;
    const qn = q.trim().toLowerCase();
    if (qn) rows = rows.filter((d) => (d.nome || "").toLowerCase().includes(qn) || (d.plataforma || "").toLowerCase().includes(qn));
    if (filterPlatform) rows = rows.filter((d) => d.plataforma === filterPlatform);
    return rows;
  }, [deals, q, filterPlatform]);
  const byStage = (key) => filtered.filter((d) => d.estagio === key);
  const totalMrr = (key) => byStage(key).reduce((a, d) => a + (d.mrr || 0), 0);

  const drop = (stageKey) => {
    if (drag) {
      const row = deals.find((r) => r.id === drag);
      if (row && row.estagio !== stageKey) move(drag, stageKey);
    }
    setDrag(null); setOver(null);
  };

  const totalCount = filtered.length;
  const totalValue = filtered.reduce((a, d) => a + (d.mrr || 0), 0);

  return (
    <>
      <PageHeader
        overline="CRM"
        title="Funil de Oportunidades"
        subtitle={pipeline ? `Funil "${pipeline.nome}" - arraste os cartoes entre etapas para atualizar` : "Sem funis configurados"}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="opps-search"
                placeholder="Pesquisar oportunidades..."
                className="pl-8 pr-3 py-1.5 text-[13px] bg-slate-100 rounded-md border border-transparent focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-blue/20 focus:outline-none w-56" />
            </div>
            {platformOptions.length > 0 && (
              <div className="inline-flex items-center gap-1.5 bg-white border border-slate-300 rounded-md px-2 py-1">
                <Building2 size={12} className="text-slate-400" />
                <select value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)}
                  data-testid="opps-filter-platform"
                  className="text-[13px] bg-transparent focus:outline-none max-w-[140px]">
                  <option value="">Todas as plataformas</option>
                  {platformOptions.map((pl) => <option key={pl} value={pl}>{pl}</option>)}
                </select>
              </div>
            )}
            {pipelines.length > 1 && (
              <div className="inline-flex items-center gap-1.5 bg-white border border-slate-300 rounded-md px-2 py-1">
                <Filter size={12} className="text-slate-400" />
                <select value={pipelineId} onChange={(e) => setPipelineId(e.target.value)} data-testid="pipeline-selector"
                  className="text-[13px] bg-transparent focus:outline-none">
                  {pipelines.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </div>
            )}
            <Btn onClick={() => nav("/app/crm/contas")} testid="opps-new-lead"><Plus size={14} /> Novo lead</Btn>
            <Btn onClick={() => nav("/app/crm/mrr-dashboard")} testid="opps-mrr-dashboard" variant="ghost"><TrendingUp size={14} /> MRR</Btn>
          </div>
        }
      />

      {/* Resumo + Legenda por plataforma (fusao numa unica barra) */}
      {totalCount > 0 && (() => {
        const visiblePlatforms = [...new Set(filtered.map((d) => d.plataforma).filter(Boolean))].sort();
        const mrrByPlatform = Object.fromEntries(
          visiblePlatforms.map((pl) => [pl, filtered.filter((d) => d.plataforma === pl).reduce((s, d) => s + (d.mrr || 0), 0)])
        );
        const mrrSemPlataforma = filtered.filter((d) => !d.plataforma).reduce((s, d) => s + (d.mrr || 0), 0);
        const multiPlatform = visiblePlatforms.length >= 2;
        return (
          <div className="mb-3 p-2.5 bg-slate-50 border border-slate-200 rounded-lg flex items-center gap-4 flex-wrap">
            {/* Totais */}
            <div className="flex items-center gap-2 text-[12px] text-slate-500 pr-4 border-r border-slate-200">
              <Target size={12} />
              <span>{totalCount} oportunidade{totalCount !== 1 ? "s" : ""}</span>
              <span className="text-slate-300">|</span>
              <span>MRR total: <b className="text-navy">{eur(totalValue)}/mes</b></span>
            </div>
            {/* Sparkline historico MRR - com seletor de funil se houver 2+ */}
            {mrrHistory.length >= 2 && (() => {
              const activeHistory = sparkPipeline && mrrHistoryByPipeline[sparkPipeline]?.length >= 2
                ? mrrHistoryByPipeline[sparkPipeline]
                : mrrHistory;
              const activePipelineName = sparkPipeline
                ? (pipelines.find((p) => p.id === sparkPipeline)?.nome || "")
                : "Todos os funis";
              const vals = activeHistory.map((p) => p.mrr);
              const last = vals[vals.length - 1];
              const prev = vals[vals.length - 2];
              const delta = last - prev;
              const pct = prev > 0 ? ((delta / prev) * 100).toFixed(1) : null;
              const up = delta >= 0;
              return (
                <div className="flex items-center gap-2 pr-4 border-r border-slate-200">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[9px] uppercase tracking-wider text-slate-400 leading-none">10 semanas</span>
                      {pipelines.length > 1 && (
                        <select value={sparkPipeline} onChange={(e) => setSparkPipeline(e.target.value)}
                          data-testid="spark-pipeline-selector"
                          className="text-[9px] bg-slate-100 rounded px-1 py-0 border-0 focus:outline-none text-slate-500 cursor-pointer leading-none">
                          <option value="">Todos os funis</option>
                          {pipelines.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                        </select>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Sparkline data={vals} points={activeHistory} width={100} height={28} />
                      {pct !== null && (
                        <span className={`text-[10px] font-semibold ${up ? "text-green-600" : "text-red-500"}`}>
                          {up ? "+" : ""}{pct}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Breakdown por plataforma - so com 2+ */}
            {multiPlatform && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Por plataforma:</span>
                {visiblePlatforms.map((pl) => {
                  const c = platformColor(pl);
                  const active = filterPlatform === pl;
                  const count = filtered.filter((d) => d.plataforma === pl).length;
                  const mrr = mrrByPlatform[pl];
                  const pct = totalValue > 0 ? Math.round((mrr / totalValue) * 100) : 0;
                  return (
                    <button key={pl}
                      onClick={() => setFilterPlatform(active ? "" : pl)}
                      data-testid={`platform-legend-${pl}`}
                      style={{ background: c.bg, color: c.text, borderColor: active ? c.text : c.border, boxShadow: active ? `0 0 0 2px ${c.text}` : "none" }}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border transition-all hover:scale-105 cursor-pointer">
                      <span style={{ background: c.text }} className="w-1.5 h-1.5 rounded-full flex-shrink-0" />
                      <span>{pl}</span>
                      <span style={{ color: c.text }} className="opacity-70 font-normal text-[10px]">{count}x</span>
                      <span style={{ background: c.text, color: c.bg }} className="rounded-full px-1.5 py-0 text-[10px] font-bold">{eur(mrr)}</span>
                      <span style={{ color: c.text }} className="opacity-60 text-[10px] font-normal">{pct}%</span>
                    </button>
                  );
                })}
                {mrrSemPlataforma > 0 && (
                  <span className="text-[11px] text-slate-400 px-2">+ {eur(mrrSemPlataforma)} sem plataforma</span>
                )}
                {filterPlatform && (
                  <button onClick={() => setFilterPlatform("")}
                    className="text-[10px] text-slate-400 hover:text-slate-600 underline ml-1">
                    limpar
                  </button>
                )}
              </div>
            )}
            {/* Plataforma unica: mostra badge simples sem breakdown */}
            {!multiPlatform && visiblePlatforms.length === 1 && (() => {
              const pl = visiblePlatforms[0];
              const c = platformColor(pl);
              return (
                <span style={{ background: c.bg, color: c.text, borderColor: c.border }}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border">
                  <span style={{ background: c.text }} className="w-1.5 h-1.5 rounded-full" />
                  {pl}
                </span>
              );
            })()}
          </div>
        );
      })()}

      {stages.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-xl p-10 text-center text-slate-500">
          <Target className="mx-auto mb-2 opacity-50" size={28} />
          <p className="text-sm">Sem etapas configuradas neste funil.</p>
          <p className="text-xs mt-1">Configure os funis em Definicoes -&gt; Funis.</p>
        </div>
      ) : (
        <div
          className="grid gap-3 overflow-x-auto pb-2"
          style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(240px, 1fr))` }}
          data-testid="pipeline-board"
        >
          {stages.map((s) => {
            const items = byStage(s.key);
            const stageMrr = totalMrr(s.key);
            const pct = totalValue > 0 ? (stageMrr / totalValue) * 100 : 0;
            const isOver = over === s.key;
            return (
              <div key={s.key}
                   onDragOver={(e) => { e.preventDefault(); setOver(s.key); }}
                   onDragLeave={() => setOver((v) => (v === s.key ? null : v))}
                   onDrop={() => drop(s.key)}
                   data-testid={`stage-col-${s.key}`}
                   className={`rounded-lg border transition-colors flex flex-col min-h-[420px] ${isOver ? "border-blue bg-blue-50/40" : "border-slate-200 bg-slate-50/40"}`}>
                <div className="px-3 pt-2 pb-0 border-b border-slate-200 sticky top-0 bg-inherit rounded-t-lg">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-navy truncate">{s.nome}</div>
                    <span className="text-[10px] font-mono text-slate-400 ml-1 flex-shrink-0">{items.length}</span>
                  </div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-semibold text-navy">{eur(stageMrr)}<span className="text-[10px] font-normal text-slate-400">/mes</span></span>
                    <span className="text-[10px] text-slate-400">{pct > 0 ? `${pct.toFixed(0)}%` : "-"}</span>
                  </div>
                  {/* Barra de progresso MRR */}
                  <div className="h-1 bg-slate-200 rounded-full mb-2 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(pct, 100)}%`, background: pct === 0 ? "transparent" : pct >= 60 ? "#0f172a" : pct >= 30 ? "#3b82f6" : "#94a3b8" }}
                    />
                  </div>
                </div>
                <div className="p-2 space-y-2 flex-1">
                  {items.length === 0 && (
                    <div className="text-[11px] text-slate-400 text-center py-6 italic">solte um cartao aqui</div>
                  )}
                  {items.map((d) => {
                    const accountNome = accounts.find((a) => a.id === d.account_id)?.nome || "";
                    return (
                    <div key={d.id}
                         draggable
                         onDragStart={() => setDrag(d.id)}
                         onDragEnd={() => { setDrag(null); setOver(null); }}
                         onClick={() => d.account_id && nav(`/app/crm/contas/${d.account_id}`)}
                         data-testid={`opp-${d.id}`}
                         className={`bg-white rounded-md border border-slate-200 border-l-4 ${probColor(d.prob)} p-2.5 shadow-sm hover:shadow-md hover:border-blue/50 cursor-pointer active:cursor-grabbing transition-all ${drag === d.id ? "opacity-40" : ""}`}>
                      <div className="text-[13px] font-semibold text-navy leading-snug line-clamp-2">{d.nome}</div>
                      {d.plataforma && (() => {
                        const c = platformColor(d.plataforma);
                        return (
                          <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}
                            className="inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none">
                            {d.plataforma}
                          </span>
                        );
                      })()}
                      {accountNome && (
                        <div className="text-[10px] text-blue mt-0.5 truncate flex items-center gap-0.5">
                          <Building2 size={9} />{accountNome}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[12px] font-bold text-navy">{eur(d.mrr)}<span className="text-[10px] text-slate-500 font-normal">/mes</span></span>
                        {d.prob != null && <Badge value={`${d.prob}%`} />}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); nav(`/app/propostas?account_id=${d.account_id || ""}&deal_id=${d.id}&titulo=${encodeURIComponent(d.nome)}&plataforma=${encodeURIComponent(d.plataforma || "")}&mrr=${encodeURIComponent(d.mrr || 0)}`); }}
                        data-testid={`opp-proposal-${d.id}`}
                        className="mt-2 w-full text-[11px] text-blue hover:bg-blue hover:text-white border border-blue/30 hover:border-blue rounded px-2 py-1 transition-colors"
                      >
                        + Gerar proposta
                      </button>
                    </div>
                  );})}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {previewProp && (
        <ProposalDrawer
          proposal={previewProp}
          open={!!previewProp}
          onClose={() => setPreviewProp(null)}
          accounts={accounts}
          onOpenPdf={async (p) => {
            try {
              const { data } = await api.get(`/cpq/proposals/${p.id}/pdf`, { responseType: "blob" });
              window.open(URL.createObjectURL(data), "_blank");
            } catch { toast.error("Nao foi possivel abrir o PDF."); }
          }}
          onOpenEditor={(p) => { setPreviewProp(null); nav(`/app/propostas?edit=${p.id}`); }}
        />
      )}
    </>
  );
}

function probColor(p) {
  if (p == null) return "border-l-slate-300";
  if (p >= 75) return "border-l-emerald-500";
  if (p >= 40) return "border-l-amber-500";
  if (p >= 20) return "border-l-sky-500";
  return "border-l-slate-300";
}
